/**
 * Paged collections, read to the end on request.
 *
 * Every view asked for one page of 200 and stopped there, so a catalog of a
 * thousand offerings silently showed the first 200 under a "200 rows" badge.
 * A continuation sends the cursor and nothing else: the cursor carries the
 * first page's revision, observation context, filters and page size, so later
 * pages stay on the same catalog even when collection publishes a newer one
 * in between. Callers pass `{ cursor }` alone for every page after the first.
 */

import { useInfiniteQuery } from "@tanstack/react-query";

interface PageEnvelope<T> {
  readonly data: readonly T[];
  readonly page?: { readonly next_cursor: string | null };
}

export function usePagedQuery<T, E extends PageEnvelope<T>>(
  key: readonly unknown[],
  fetchPage: (cursor: string | null) => Promise<E>,
  options: { readonly enabled?: boolean } = {},
) {
  const query = useInfiniteQuery({
    queryKey: key,
    enabled: options.enabled ?? true,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last) => last.page?.next_cursor ?? undefined,
  });
  return {
    query,
    rows: (query.data?.pages ?? []).flatMap((page) => page.data) as T[],
    /** The first page carries the revision and context every later page is pinned to. */
    first: query.data?.pages[0],
  };
}

/**
 * The paging footer: how much is shown, and the way to see the rest. It never
 * claims a total, because the API does not count one.
 */
export function describeShown(count: number, more: boolean, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}${more ? " shown, more available" : ""}`;
}
